import { createRef, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router";
import { useNavigate } from "react-router-dom";
import { FormInput } from "../../../components/FormInput";
import FormNumberInput from "../../../components/FormNumberInput";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { classNames } from "primereact/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SelectButton } from "primereact/selectbutton";
import PrimeConfirmDialog from "../../../components/dialog/PrimeConfirmDialog";
import PrimeDialog from "../../../components/dialog/PrimeDialog";
import { FormTextarea } from "../../../components/FormTextarea";
import { useEditableFlag } from "../../../hooks/useEditableFlag";
import { SystemLoading, checkLoading } from "../../../store/loadingStore";
import { MessageContext, handleSuccessMessage } from "../../../store/messageStore";
import { AlertService } from "../../../utility/service/alert-service";
import { deleteMultipartFile, uploadMultipartFile } from "../../../utility/service/api-file";
import { getSubItemsByItemNo } from "../../../utility/service/api-service";
import {
  CreateActivityDataList,
  DeleteActivityData,
  F1010332Before,
  approveWork,
  updateActivityDataList,
} from "../../../utility/service/carbon-service";
import { queryCarbonCoefficientById } from "../../../utility/service/carbonFootprint-service";
import {
  Base64Encoder,
  BcryptEncoder,
  checkDpSuccess,
  getFileBase64,
  splitFileExtension,
  splitFileName,
} from "../../../utility/service/tool-service";
import { addFormValidator } from "../../../utility/validate/formValidator";
import CoefficientInfo from "../../inventoryCarbonFootprint/cusComponents/CoefficientInfo";
import CycleDataTable from "./CycleDataTable";
import FileAttachmentHandler from "./FileAttachmentHandler";

/**
 * activeIndex 返回頁籤Index (明細頁 返回活動數據頁)
 * **/
const ActivityData_Detail = ({ activeIndex: propActiveIndex }) => {
  const { t } = useTranslation();
  const isEditable = useEditableFlag("F10103"); // control edit by F10103
  const navigate = useNavigate();
  const { setAction, action, setItemObj, itemObj } = useOutletContext();

  // 根據是否有重大性評估動態計算活動數據頁面的正確 index
  // 如果沒有重大性評估：活動數據頁面的 index 是 3
  // 如果有重大性評估：活動數據頁面的 index 是 4
  const activeIndex = useMemo(() => {
    if (propActiveIndex !== undefined && propActiveIndex !== null) {
      return propActiveIndex;
    }
    // 根據 itemObj?.importanceIdentification 來決定
    return itemObj?.importanceIdentification === "Y" ? 4 : 3;
  }, [propActiveIndex, itemObj?.importanceIdentification]);
  const refs = useRef([]);
  const [unitValue, setUnitValue] = useState("");
  const [unitWord, setUnitWord] = useState();
  const [cycle, setCycle] = useState("");
  const [emissionUnitList, setEmissionUnitList] = useState([]);
  const [methodList, setMethodList] = useState([]);
  const [cycleList, setCycleList] = useState([]);
  const [rows, setRows] = useState();
  const [uncertaintyFile, setUncertaintyFile] = useState(null);
  const uncertaintyFileRef = useRef(null);
  const [delDialog, setDelDialog] = useState(false);
  const [, dispatch] = useContext(MessageContext); //訊息視窗
  const [, loading] = useContext(SystemLoading);
  //v0.75 外購電力類別
  const [showElecType, setShowElecType] = useState(false);
  const [elecTypeList, setElecTypeList] = useState([]);
  const [trustTypeList, setTrustTypeList] = useState([]);
  //建立按鈕 isLoading
  const [confirmLoad, setConfirmLoad] = useState(false);
  const [formRule, setFormRule] = useState([]); //表單驗證
  const [coefficientObj, setCoefficientObj] = useState({}); //類四 排放源明細
  const [detailDialog, setDetailDialog] = useState(false);
  const disabledCondition = !isEditable || action === "detail" || action === "approve";
  const isApprove = action === "approve";
  const isNoPermissionResponse = (res) => res?.data?.ResHeader?.rtnCode === "_0407";

  const approveOptions = [
    { label: t("approveAction.C"), value: "C" },
    { label: t("approveAction.RTA"), value: "RTA" },
  ];

  const applyOptions = [{ label: t("approveAction.A"), value: "C" }];

  // 設置標記，表示從子頁面返回
  useEffect(() => {
    sessionStorage.setItem("fromCarbonChildPage", "true");
  }, []);

  const periodList = {
    Y: [t("timeCycle.y")],
    H: [t("timeCycle.h1"), t("timeCycle.h2")],
    Q: [t("timeCycle.q1"), t("timeCycle.q2"), t("timeCycle.q3"), t("timeCycle.q4")],
    M: [
      t("Jan"),
      t("Feb"),
      t("Mar"),
      t("Apr"),
      t("May"),
      t("Jun"),
      t("Jul"),
      t("Aug"),
      t("Sep"),
      t("Oct"),
      t("Nov"),
      t("Dec"),
    ],
  };

  const {
    register,
    handleSubmit,
    watch,
    clearErrors,
    getValues,
    setValue,
    resetField,
    trigger,
    reset: resetForm,
    control,
    reset,
    formState: { errors, isValid, isDirty },
  } = useForm({
    defaultValues: {},
    //錯誤驗證時機
    mode: "all",
  });

  const inputCheckKeyDown = (e) => {
    //避免觸發 按enter 後 就觸發 onSubmit 事件冒泡 給input 使用 <FormInput enterClick事件 ; 一般input onKeyDown事件
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  const onSubmit = (data) => {
    setConfirmLoad(true);
    let dataList = [];
    dataList = rows?.map((item, key) => {
      return {
        period: key + 1,
        amount: item.amount,
      };
    });
    let reqBody = {
      identifySeq: itemObj?.data?.identifySeq,
      unit: Base64Encoder(BcryptEncoder(data.unit)) + "," + findUnitIndex(data.unit),
      method: Base64Encoder(BcryptEncoder(data.method)) + "," + findMethodIndex(data.method),
      cycle: Base64Encoder(BcryptEncoder(data.cycle)) + "," + findCycleIndex(data.cycle),
      dataList: dataList,
      // v0.75
      provider: data.provider,
      dataRatio: data.dataRatio,
      dataSource: data.dataSource,
      dataSaver: data.dataSaver,
      trustType: Base64Encoder(BcryptEncoder(data.trustType)) + "," + findTrustTypeIndex(data.trustType),
      //不確定性 06/13
      ciLower: data.ciLower,
      ciUpper: data.ciUpper,
      uncertaintySource: data.uncertaintySource,
      uncertaintySaver: data.uncertaintySaver,
      emissionDataRemark: data.emissionDataRemark,
      isUseDevice: data.isUseDevice,
      fuelSource: data.fuelSource,
      fuelDataSource: data.fuelDataSource,
      unitRatio: data.unitRatio,
    };

    // v0.75
    if (showElecType) {
      //如果是外購電力類別
      reqBody.elecType = Base64Encoder(BcryptEncoder(data.elecType)) + "," + findElecTypeIndex(data.elecType);
    } else {
      reqBody.elecType = "";
    }

    if (itemObj?.data?.dataList?.length === 0) {
      //建立
      CreateActivityDataList(reqBody)
        .then((res) => {
          if (checkDpSuccess(res)) {
            navigate("/management/carbonFootprint/detail", { state: { activeIndex: activeIndex } });
            handleSuccessMessage(dispatch, t("button.create") + " " + t("message.success"));
            setAction("detail");
            setConfirmLoad(false);
          } else if (isNoPermissionResponse(res)) {
            navigate("/management/carbonFootprint/detail", { state: { activeIndex: activeIndex } });
          }
        })
        .finally((fin) => {
          setConfirmLoad(false);
        });
    } else {
      let updateDataList = [];
      updateDataList = rows?.map((item, key) => {
        return {
          dataSeq: item.dataSeq,
          unit: Base64Encoder(BcryptEncoder(data.unit)) + "," + findUnitIndex(data.unit),
          method: Base64Encoder(BcryptEncoder(data.method)) + "," + findMethodIndex(data.method),
          amount: item.amount,
          provider: data.provider,
          dataRatio: data.dataRatio,
          dataSource: data.dataSource,
          dataSaver: data.dataSaver,
          trustType: Base64Encoder(BcryptEncoder(data.trustType)) + "," + findTrustTypeIndex(data.trustType),
          elecType: showElecType
            ? Base64Encoder(BcryptEncoder(data.elecType)) + "," + findElecTypeIndex(data.elecType)
            : "",
          // v0.75 不確定性06/13
          ciLower: data.ciLower,
          ciUpper: data.ciUpper,
          uncertaintySource: data.uncertaintySource,
          uncertaintySaver: data.uncertaintySaver,
          emissionDataRemark: data.emissionDataRemark,
          isUseDevice: data.isUseDevice,
          fuelSource: data.fuelSource,
          fuelDataSource: data.fuelDataSource,
          unitRatio: data.unitRatio,
        };
      });

      updateActivityDataList({ dataList: updateDataList })
        .then((res) => {
          if (checkDpSuccess(res)) {
            setConfirmLoad(false);
            navigate("/management/carbonFootprint/detail", { state: { activeIndex: activeIndex } });
            handleSuccessMessage(dispatch, t("button.update") + " " + t("message.success"));
            setAction("detail");
          } else if (isNoPermissionResponse(res)) {
            navigate("/management/carbonFootprint/detail", { state: { activeIndex: activeIndex } });
          }
        })
        .finally((fin) => {
          setConfirmLoad(false);
        });
    }
  };

  const axios_approveWork = () => {
    approveWork({
      identifySeq: itemObj?.data?.identifySeq,
      action: getValues("action"),
      memo: getValues("memo"),
    })
      .then((res) => {
        if (checkDpSuccess(res)) {
          navigate("/management/carbonFootprint/detail", { state: { activeIndex: activeIndex } });
          handleSuccessMessage(dispatch, t("button.approve") + " " + t("message.success"));
          setValue("action", "");
          setValue("memo", "");
          setAction("detail");
        }
      })
      .finally(() => checkLoading(loading, false));
  };

  const axios_uploadFile = (indexOrType, file, fileName, fileExtension) => {
    checkLoading(loading);
    const isUncertainty = indexOrType === "uncertainty";
    const formData = new FormData();
    //v0.5版
    // formData.append("identifySeq",itemObj?.data?.identifySeq);
    // formData.append("period",period);
    // formData.append("provingData",provingData);
    // formData.append("fileName",fileName);
    // formData.append("fileExtension",fileExtension);

    //call 共用上傳API API好了開啟這裡
    formData.append("fileName", fileName);
    formData.append("fileExtension", fileExtension);
    formData.append("attachFile", file);
    formData.append("refId", itemObj?.data?.identifySeq);
    formData.append("companySeq", itemObj?.companySeq);

    formData.append("filePath", isUncertainty ? "uncertainty/" : `cfv_plan_emission_data/${indexOrType + 1}/`);
    formData.append(
      "fileCateCode",
      isUncertainty ? "cfv_plan_emission_data_uncertainty_related" : "cfv_plan_emission_data",
    );

    uploadMultipartFile(formData)
      .then((res) => {
        if (checkDpSuccess(res)) {
          let zoomSrc = fileExtension === "pdf" || fileExtension === "zip" ? "" : URL.createObjectURL(file);

          const fileData = {
            fileExtension: fileExtension,
            fileName: fileName,
            previewUrl: URL.createObjectURL(file),
            zoomSrc: zoomSrc,
            fileId: res.data?.RespBody.fileId,
            filePath: URL.createObjectURL(file),
            filePathSml: URL.createObjectURL(file),
            fileInfo: { type: fileExtension, name: fileName },
          };

          if (isUncertainty) {
            setUncertaintyFile(fileData);
          } else {
            setRows((pre) => {
              const newRows = [...pre];
              newRows[indexOrType] = { ...newRows[indexOrType], ...fileData };
              return newRows;
            });
          }

          handleSuccessMessage(dispatch, t("button.upload") + " " + t("message.success"));
        }
      })
      .finally((fin) => {
        checkLoading(loading, false);
      });

    // uploadProvingDataByFormData(formData)
    //     .then((res)=>{
    //         if(checkDpSuccess(res)){
    //             setRows((pre)=>{
    //                 pre[period-1].fileExtension = fileExtension;
    //                 pre[period-1].fileName = fileName;
    //                 pre[period-1].previewUrl = URL.createObjectURL(file); //註解
    //                 // pre[period-1].previewUrl = dataURItoBlob(combinePrefixBase64Image(provingData,fileExtension)) //測試 可以用 base64 轉回blob格式
    //                 pre[period-1].fileInfo = {
    //                     type:fileExtension,
    //                     name:fileName
    //                 }
    //                 return [ ...pre ]
    //             })
    //             handleSuccessMessage(dispatch, t('button.upload') + ' ' + t('message.success'));

    //         }
    //     }).finally(fin=>{ checkLoading(loading, false);})
  };

  const handleDataCreateOrUpdate = () => {
    if (itemObj?.data?.dataList?.length > 0) {
      setValue("cycle", itemObj?.data?.cycle, { shouldValidate: true });
      setValue("method", itemObj?.data?.method, { shouldValidate: true });
      setValue("unit", itemObj?.data?.unit, { shouldValidate: true });
      //v0.75
      setValue("elecType", itemObj?.data?.elecType);
      setValue("provider", itemObj?.data?.provider);
      setValue("dataRatio", itemObj?.data?.dataRatio, { shouldValidate: true });
      setValue("dataSource", itemObj?.data?.dataSource);
      setValue("dataSaver", itemObj?.data?.dataSaver);
      setValue("trustType", itemObj?.data?.trustType, { shouldValidate: true });
      //v0.75 不確定性
      setValue("ciLower", itemObj?.data?.ciLower);
      setValue("ciUpper", itemObj?.data?.ciUpper);
      setValue("uncertaintySource", itemObj?.data?.uncertaintySource);
      setValue("uncertaintySaver", itemObj?.data?.uncertaintySaver);
      setValue("emissionDataRemark", itemObj?.data?.emissionDataRemark);
      setValue("isUseDevice", itemObj?.data?.isUseDevice);
      setValue("fuelSource", itemObj?.data?.fuelSource);
      setValue("fuelDataSource", itemObj?.data?.fuelDataSource);
      setValue("unitRatio", itemObj?.data?.unitRatio);

      let newRowArray = [];
      let previewUrl = "";
      let zoomSrc = "";
      let fileInfo = {};
      itemObj?.data?.dataList?.map((item, index) => {
        fileInfo = {};
        setValue(`amount_${index}`, item?.amount, { shouldValidate: true });
        setValue(`provingDataPath_${index}`, item?.provingDataPath);
        setValue(`fileExtension_${index}`, item.fileExtension);
        setValue(`fileName_${index}`, item.fileName);
        //如果有值需轉換成圖片顯示 如果副檔名不為zip或空值
        if (item?.fileExtension !== "zip" && item?.fileExtension !== "" && item?.fileExtension !== null) {
          if (item?.fileExtension === "pdf") {
            // previewUrl =  handelBase64PdfToBlob(item?.provingData);
            //API 好了開啟這裡
            previewUrl = item?.filePathSml;
            zoomSrc = "";
          } else {
            // previewUrl = (combinePrefixBase64Image(item?.provingData,item?.fileExtension));//圖片可以不用轉回 blob格式
            //API 好了開啟這裡
            previewUrl = item?.filePathSml;
            zoomSrc = item?.filePath;
          }
          // console.log(previewUrl);
          fileInfo = {
            name: item?.fileName,
            type: item?.fileExtension,
          };
        } else {
          fileInfo = null;
        }
        newRowArray.push({
          ...item,
          period: periodList[itemObj?.data?.cycle][index],
          amount: item?.amount, //數據量
          // provingData: item?.provingDataPath, //佐證資料
          fileExtension: item.fileExtension, //檔案副檔名
          previewUrl: previewUrl,
          zoomSrc: zoomSrc,
          fileInfo: fileInfo,
          fileId: item?.fileId,
          filePathSml: item?.filePathSml,
          filePath: item?.filePath,
        });
      });
      setRows(newRowArray);

      if (itemObj?.data?.uncertaintyFile && itemObj?.data?.uncertaintyFile.fileId) {
        const u = itemObj.data.uncertaintyFile;
        let uPreviewUrl = "";
        let uZoomSrc = "";
        let uFileInfo = null;

        if (u.fileExtension !== "zip" && u.fileExtension !== "" && u.fileExtension !== null) {
          if (u.fileExtension === "pdf") {
            uPreviewUrl = u.filePath;
            uZoomSrc = "";
          } else {
            uPreviewUrl = u.filePathSml;
            uZoomSrc = u.filePath;
          }
          uFileInfo = { name: u.fileName, type: u.fileExtension };
        }

        setUncertaintyFile({
          ...u,
          previewUrl: uPreviewUrl,
          zoomSrc: uZoomSrc,
          fileInfo: uFileInfo,
        });
      } else {
        setUncertaintyFile(null);
      }

      if (!refs.current.length) {
        refs.current = Array(periodList[itemObj?.data?.cycle]?.length)
          .fill(null)
          .map((_, i) => refs.current[i] || createRef());
      }
    } else {
      //例外判斷如果geptype為 M 就鎖死 cycle 為 月
      if (itemObj?.gwpType === "M") {
        setValue("cycle", "M");
        setCycle("M");
        if (!refs.current.length) {
          refs.current = Array(12)
            .fill(null)
            .map((_, i) => refs.current[i] || createRef());
        }
      }
      setValue("dataRatio", "100", { shouldValidate: true });
      // v0.75 ratio 參數去留 待討論
      // if(itemObj?.places!==null){
      //     if(itemObj?.places?.ratio!==null){
      //         setValue("dataRatio",itemObj?.places?.ratio, { shouldValidate: true });
      //     }else{
      //         setValue("dataRatio",100, { shouldValidate: true });
      //     }
      // }else{
      // setValue("dataRatio",100, { shouldValidate: true });
      // }
    }
  };

  const dataRatioWatch = watch("dataRatio");

  useEffect(() => {
    if (dataRatioWatch !== undefined) {
      trigger("emissionDataRemark");
    }
  }, [dataRatioWatch, trigger]);

  useEffect(() => {
    if (JSON.stringify(itemObj) === "{}") {
      navigate("/management/carbonFootprint");
    } else {
      handleDataCreateOrUpdate();
    }
    //外購電力類別 需 call 清單
    if (itemObj?.data?.typeSeq === "0_0000000b") {
      setShowElecType(true); //顯示外購電力類別
      axios_getElecTypeList();
    }
  }, [itemObj]);

  useEffect(() => {
    axios_getDropdownData(); //抓下拉清單
    if (!refs.current.length) {
      refs.current = Array(periodList[getValues("cycle")]?.length)
        .fill(null)
        .map((_, i) => refs.current[i] || createRef());
    }
    axios_getCreateBefore(); //統一檢核
  }, []);

  //建立前查詢form 規則
  const axios_getCreateBefore = () => {
    F1010332Before().then((res) => {
      if (checkDpSuccess(res)) {
        let array = [];
        res?.data?.RespBody?.constraints?.forEach((item) => {
          const result = addFormValidator(item?.field, res.data.RespBody.constraints);
          array.push(result);
        });
        setFormRule(array);
      }
    });
  };

  const setValidForm = useCallback(
    (formId) => {
      if (formRule?.length > 0) {
        const tmp = formRule?.find((item) => {
          return Object.keys(item)[0] === formId;
        });
        return tmp ? tmp[formId] : "";
      }
    },
    [formRule],
  );

  const confirmDel = () => {
    DeleteActivityData({
      identifySeq: [itemObj?.data?.identifySeq],
    }).then((res) => {
      if (checkDpSuccess(res)) {
        setDelDialog(false);
        setItemObj((pre) => {
          return {
            ...pre,
            data: {
              ...pre.data,
              dataList: [],
            },
          };
        });
        setCycle(getValues("cycle"));
        setValue("dataSource", "");
        setValue("dataSaver", "");
        setValue("trustType", "");
        setValue("elecType", "");
        //v0.75 不確定性
        setValue("ciLower", "");
        setValue("ciUpper", "");
        setValue("uncertaintySource", "");
        setValue("uncertaintySaver", "");

        handleSuccessMessage(dispatch, t("button.delete") + " " + t("message.success"));
      } else if (isNoPermissionResponse(res)) {
        navigate("/management/carbonFootprint/detail", { state: { activeIndex: activeIndex } });
      }
    });
  };

  const refuseDelete = () => {
    handleDataCreateOrUpdate();
    setDelDialog(false);
  };

  const findUnitIndex = (unit) => {
    let result = emissionUnitList?.filter((item) => item.subitemNo === unit);
    return result.length > 0 ? result[0].index : false;
  };

  const findMethodIndex = (method) => {
    let result = methodList?.filter((item) => item.subitemNo === method);
    return result.length > 0 ? result[0].index : false;
  };

  const findCycleIndex = (cycle) => {
    let result = cycleList?.filter((item) => item.subitemNo === cycle);
    return result.length > 0 ? result[0].index : false;
  };

  const findElecTypeIndex = (elecType) => {
    let result = elecTypeList?.filter((item) => item.subitemNo === elecType);
    return result.length > 0 ? result[0].index : false;
  };

  const findTrustTypeIndex = (trustType) => {
    let result = trustTypeList?.filter((item) => item.subitemNo === trustType);
    return result.length > 0 ? result[0].index : false;
  };

  const handleCycleChange = (cycle) => {
    //如果本來就有資料 切換cycle 就要提示
    if (itemObj?.data?.dataList?.length > 0 && itemObj?.data?.cycle !== cycle) {
      setDelDialog(true);
      setValue("cycle", cycle);
    } else {
      setCycle(cycle);
    }
  };

  // v0.75
  const axios_getElecTypeList = () => {
    //外購電力
    getSubItemsByItemNo("CFV_ELEC_TYPE", 14).then((res) => {
      if (checkDpSuccess(res)) {
        let data = res.data.RespBody.subItems;
        let newArray = data.map((item, index) => {
          return {
            ...item,
            index: index,
          };
        });
        setElecTypeList(newArray);
      }
    });
  };

  const axios_getDropdownData = () => {
    //單位
    let emissionUnit =
      itemObj?.data?.classNo === "0_00000001" ||
      itemObj?.data?.classNo === "0_00000002" ||
      itemObj?.data?.classNo === "0_00000003";
    let unitName = emissionUnit ? "CFV_EMISSION_UNIT" : "COEFFICIENT_UNIT";
    let unitIndex = emissionUnit ? 7 : 27;
    getSubItemsByItemNo(unitName, unitIndex).then((res) => {
      if (checkDpSuccess(res)) {
        let data = res.data.RespBody.subItems;
        let newArray = data.map((item, index) => {
          return {
            ...item,
            index: index,
          };
        });
        setEmissionUnitList(newArray);
      }
    });

    //測量方式
    getSubItemsByItemNo("CFV_MEAS_METHOD", 8).then((res) => {
      if (checkDpSuccess(res)) {
        let data = res.data.RespBody.subItems;
        let newArray = data.map((item, index) => {
          return {
            ...item,
            index: index,
          };
        });
        setMethodList(newArray);
      }
    });

    //計算週期
    getSubItemsByItemNo("CFV_CALC_CYCLE", 9).then((res) => {
      if (checkDpSuccess(res)) {
        let data = res.data.RespBody.subItems;
        let newArray = data.map((item, index) => {
          return {
            ...item,
            index: index,
          };
        });
        setCycleList(newArray);
      }
    });

    //可信種類
    getSubItemsByItemNo("CFV_TRUST_TYPE", 15).then((res) => {
      if (checkDpSuccess(res)) {
        let data = res.data.RespBody.subItems;
        let newArray = data.map((item, index) => {
          return {
            ...item,
            index: index,
          };
        });
        setTrustTypeList(newArray);
      }
    });
  };

  const checkKeyDown = (e) => {
    if (e.target instanceof HTMLTextAreaElement) {
      return;
    }
    if (e.key === "Enter") e.preventDefault();
  };

  const handleFileChange = (e, ref, indexOrType) => {
    //每次開啟選擇檔案觸發onChange事件
    if (e.target.files[0]) {
      if (e.target.files[0].size >= 20000000) {
        //大於20MB
        AlertService.ok("Error", "Size over 20MB");
        ref.current.value = null;

        if (typeof indexOrType === "number") {
          setRows((pre) => {
            pre[indexOrType].fileExtension = "";
            pre[indexOrType].fileName = "";
            pre[indexOrType].provingDataPath = "";
            pre[indexOrType].previewUrl = "";
            pre[indexOrType].zoomSrc = "";
            return [...pre];
          });
        }
      } else {
        getFileBase64(e.target.files[0]).then((result) => {
          const fileName = splitFileName(e.target.files[0]?.name);
          const fileExtension = splitFileExtension(e.target.files[0]?.name);
          const file = e.target.files[0];
          axios_uploadFile(indexOrType, file, fileName, fileExtension);

          // setRows((pre)=>{ //此段註解 上傳成功才顯示圖片上去
          //     pre[index].fileExtension = fileExtension;
          //     pre[index].fileName = e.target.files[0]?.name?.split('.')[0];
          //     pre[index].previewUrl = URL.createObjectURL(e.target.files[0]); //註解
          //     // pre[index].previewUrl = dataURItoBlob(combinePrefixBase64Image(provingData,fileExtension)) //測試 可以用 base64 轉回blob格式
          //     pre[index].fileInfo = {
          //         type:fileExtension,
          //         name:e.target.files[0]?.name?.split('.')[0]
          //     }
          //     return [ ...pre ]
          // })
          // console.log(combinePrefixBase64Image(provingData,fileExtension)); //將base64Str 轉回 圖片base64格式
          // console.log(dataURItoBlob(combinePrefixBase64Image(provingData,fileExtension)));
        });
      }
    }
  };

  const handleDeleteImg = (e, ref, indexOrType, fileId) => {
    //刪除圖片
    checkLoading(loading);
    deleteMultipartFile({ fileId })
      .then((res) => {
        if (checkDpSuccess(res)) {
          if (indexOrType === "uncertainty") {
            setUncertaintyFile(null);
          } else {
            setRows((pre) => {
              pre[indexOrType].fileId = null;
              pre[indexOrType].fileName = "";
              pre[indexOrType].fileExtension = "";
              pre[indexOrType].filePath = "";
              pre[indexOrType].filePathSml = "";
              pre[indexOrType].previewUrl = "";
              return [...pre];
            });
          }
          handleSuccessMessage(dispatch, t("button.delete") + " " + t("message.success"));
        } else {
          AlertService.ok(res?.data?.ResHeader?.rtnMsg);
        }
      })
      .finally((fin) => {
        checkLoading(loading, false);
      });
  };

  const searchCoefficientId = (coefficientId) => {
    checkLoading(loading);
    queryCarbonCoefficientById({
      coefficientId,
    })
      .then((res) => {
        if (checkDpSuccess(res)) {
          let data = res?.data?.RespBody;
          setCoefficientObj(data);
          setDetailDialog(true);
        }
      })
      .finally((fin) => {
        checkLoading(loading, false);
      });
  };

  const isNotBlank = (str) => {
    return str !== "" && str !== null && str !== undefined;
  };

  return (
    <>
      {/* 上方基本資訊 */}
      <div className="flex justify-content-between flex-wrap align-items-center">
        <div className="align-items-start">
          <Button
            rounded
            text
            onClick={() => {
              navigate("/management/carbonFootprint/detail", { state: { activeIndex: activeIndex } });
              setAction("detail");
            }}
          >
            <FontAwesomeIcon icon="fa-solid fa-arrow-left-long" />
            {t("button.return_to_list")}
          </Button>
        </div>
        <div className="align-items-end">
          {itemObj?.data?.dataList?.length === 0 ? t("button.create") : t("button.detail")}
        </div>
      </div>
      <div className="p-card bg-gray">
        <div className="p-card-body">
          <div className="grid">
            <div className="col-12">
              {/* {JSON.stringify(rows)} */}
              <h3 className="m-0">
                {itemObj?.companyName} {itemObj?.year} {t("inventory_plan")}{" "}
              </h3>
            </div>
            <div className="col-12 lg:col-6">
              <label className="label-has-icon">{t("place")}</label>
              <span
                style={{ display: "contents" }}
                data-pr-position="top"
                data-pr-tooltip={itemObj?.data?.remark}
                className="remarkBody text-ellipsis"
              >
                {itemObj?.data?.placeName}
              </span>
            </div>
            <div className="col-12 lg:col-6">
              <label className="label-has-icon">{t("category")}</label>
              <span>{itemObj?.data?.typeName}</span>
            </div>
            <div className="col-12 lg:col-6">
              <label className="label-has-icon">{t("equipment")}</label>
              <span>{itemObj?.data?.deviceName}</span>
            </div>
            <div className="col-12 lg:col-6">
              <label className="label-has-icon">{t("emission_source")}</label>
              <span>{itemObj?.data?.sourceName}</span>
              {itemObj?.data?.sourceInfo !== null &&
                (itemObj?.classNoInfoData?.classNo === "0_00000001" ||
                  itemObj?.classNoInfoData?.classNo === "0_00000002" ||
                  itemObj?.classNoInfoData?.classNo === "0_00000003") && (
                  <Button
                    className="p-1"
                    size="small"
                    type="button"
                    rounded
                    text
                    tooltip={itemObj?.data?.sourceInfo}
                    tooltipOptions={{ position: "bottom" }}
                  >
                    <FontAwesomeIcon icon="fa-solid fa-circle-question" />
                  </Button>
                )}
              {// 將 classNo 4 的規則擴展到 4、5、6
              (itemObj?.classNoInfoData?.classNo === "0_00000004" ||
                itemObj?.classNoInfoData?.classNo === "0_00000005" ||
                itemObj?.classNoInfoData?.classNo === "0_00000006") && (
                <Button
                  className="p-1"
                  size="small"
                  type="button"
                  rounded
                  text
                  tooltip={t("button.detail")}
                  tooltipOptions={{ position: "bottom" }}
                  onClick={() => {
                    searchCoefficientId(itemObj?.data?.sourceSeq);
                  }}
                >
                  {/* <FontAwesomeIcon icon="fa-solid fa-shoe-prints" size="xs" />   */}
                  <FontAwesomeIcon icon="fa-solid fa-magnifying-glass" />
                </Button>
              )}
            </div>
            <div className="col-12">
              <label className="label-has-icon">{t("remark")}</label>
              <span
                style={{ display: "contents" }}
                data-pr-position="top"
                data-pr-tooltip={itemObj?.data?.remark}
                className="remarkBody text-ellipsis"
              >
                {itemObj?.data?.remark}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div className="p-card">
        <div className="p-card-body">
          <form className="grid" onSubmit={handleSubmit(onSubmit)} onKeyDown={checkKeyDown}>
            {/* 單位 */}
            <div className="col-12 lg:col-4">
              <label htmlFor="unit" className="form-label required">
                {t("unit")}
              </label>
              <Controller
                name="unit"
                control={control}
                rules={setValidForm("unit")}
                render={({ field, fieldState }) => (
                  <Dropdown
                    filter
                    appendTo={"self"}
                    optionLabel="subitemName"
                    optionValue="subitemNo"
                    value={field.value}
                    defaultValue={""}
                    placeholder={t("plz_chs")}
                    name="unit"
                    control={control}
                    options={emissionUnitList}
                    className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                    disabled={disabledCondition}
                    onChange={(event) => {
                      field.onChange(event.value);
                      // if (isNotBlank(event.value) && event.value === itemObj?.data?.sourceUnit) {
                      //   setValue("unitRatio", "", { shouldValidate: true, shouldDirty: true });
                      // }
                    }}
                  />
                )}
              />
            </div>
            {/* 轉換比例 */}
            {isNotBlank(watch("unit")) && watch("unit") !== itemObj?.data?.sourceUnit && (
              <div className="col-12 lg:col-4">
                <FormNumberInput
                  labelClass=""
                  withLabel={true}
                  register={register}
                  errors={errors}
                  id="unitRatio"
                  labelText={t("conversionRatio")}
                  disabled={disabledCondition}
                  enterClick={inputCheckKeyDown}
                  value={watch("unitRatio")}
                  onChange={(newValue) => setValue("unitRatio", newValue)}
                  placeholder={`1${emissionUnitList.find((unit) => unit.subitemNo === watch("unit"))?.subitemName ||
                    watch("unit")} ＝ ? ${emissionUnitList.find((unit) => unit.subitemNo === itemObj?.data?.sourceUnit)
                    ?.subitemName || itemObj?.data?.sourceUnit}`}
                />
                {/* 當有轉換比例值時顯示實際轉換關係 */}
                {watch("unitRatio") && watch("unitRatio") !== "" && (
                  <div className="text-xs text-blue-600 mt-1 font-medium">
                    1 {emissionUnitList.find((unit) => unit.subitemNo === watch("unit"))?.subitemName || watch("unit")}{" "}
                    ＝ {watch("unitRatio")}{" "}
                    {emissionUnitList.find((unit) => unit.subitemNo === itemObj?.data?.sourceUnit)?.subitemName ||
                      itemObj?.data?.sourceUnit}
                  </div>
                )}
              </div>
            )}
            {/* 量測方式 */}
            <div className="col-12 lg:col-4">
              <label htmlFor="method" className="form-label required">
                {t("meas_method")}
              </label>
              <Controller
                name="method"
                control={control}
                rules={setValidForm("method")}
                render={({ field, fieldState }) => (
                  <Dropdown
                    appendTo={"self"}
                    optionLabel="subitemName"
                    optionValue="subitemNo"
                    value={field.value}
                    defaultValue={""}
                    placeholder={t("plz_chs")}
                    name="method"
                    control={control}
                    options={methodList}
                    className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                    disabled={disabledCondition}
                    onChange={(event) => {
                      field.onChange(event.value);
                    }}
                  />
                )}
              />
            </div>
            {/* 計算週期 */}
            <div className="col-12 lg:col-4">
              <label htmlFor="cycle" className="form-label required">
                {t("calc_cycle")}
              </label>
              <Controller
                name="cycle"
                control={control}
                rules={setValidForm("cycle")}
                render={({ field, fieldState }) => (
                  <Dropdown
                    disabled={itemObj?.gwpType === "M" || disabledCondition}
                    appendTo={"self"}
                    optionLabel="subitemName"
                    optionValue="subitemNo"
                    value={field.value}
                    defaultValue={""}
                    placeholder={t("plz_chs")}
                    name="cycle"
                    control={control}
                    options={cycleList}
                    className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                    onChange={(event) => {
                      handleCycleChange(event.value);
                      field.onChange(event.value);
                      // setCycle(event.value);
                    }}
                  />
                )}
              />
            </div>
            {/* 類別二 外購電力類別 */}
            {showElecType && (
              <div className="col-12 lg:col-4">
                <label htmlFor="elecType" className="form-label required">
                  {t("elecType")}
                </label>
                <Controller
                  name="elecType"
                  control={control}
                  rules={{ required: { value: showElecType ? true : false, message: t("validation.required") } }}
                  render={({ field, fieldState }) => (
                    <Dropdown
                      appendTo={"self"}
                      optionLabel="subitemName"
                      optionValue="subitemNo"
                      value={field.value}
                      defaultValue={""}
                      placeholder={t("plz_chs")}
                      name="elecType"
                      disabled={disabledCondition}
                      control={control}
                      options={elecTypeList}
                      className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                      onChange={(event) => {
                        field.onChange(event.value);
                      }}
                    />
                  )}
                />
              </div>
            )}
            {itemObj?.classNoInfoData?.classNo === "2" && (
              <div className="col-12 lg:col-4">
                <label htmlFor="provider" className="form-label">
                  {t("provider_name")}
                </label>
                <FormInput
                  register={register}
                  errors={errors}
                  id="provider"
                  name="provider"
                  type="text"
                  disabled={disabledCondition}
                  enterClick={inputCheckKeyDown}
                />
              </div>
            )}
            {/* 分配比率 */}
            <div className="col-12 lg:col-4">
              <label htmlFor="dataRatio" className="form-label">
                {t("data_ratio")} %
              </label>
              <FormNumberInput
                withLabel={false}
                register={register}
                errors={errors}
                id="dataRatio"
                disabled={disabledCondition}
                rules={setValidForm("dataRatio")}
                enterClick={inputCheckKeyDown}
                value={watch("dataRatio")}
                onChange={(newValue) => setValue("dataRatio", newValue)}
              />
            </div>
            {/* 數據來源表單名稱 */}
            <div className="col-12 lg:col-4">
              <label htmlFor="dataSource" className="form-label">
                {t("dataSource_form")}
              </label>
              <FormInput
                disabled={disabledCondition}
                withLabel={false}
                register={register}
                errors={errors}
                id="dataSource"
                type="text"
                enterClick={inputCheckKeyDown}
              />
            </div>
            {/* 保存單位 */}
            <div className="col-12 lg:col-4">
              <label htmlFor="dataSaver" className="form-label">
                {t("dataSaver")}
              </label>
              <FormInput
                disabled={disabledCondition}
                withLabel={false}
                register={register}
                errors={errors}
                id="dataSaver"
                type="text"
                enterClick={inputCheckKeyDown}
              />
            </div>
            {/* 活動數據可信種類(儀器校正誤差等級) */}
            <div className="col-12 lg:col-4">
              <label htmlFor="trustType" className="form-label required">
                {t("trustType")}
              </label>
              <Controller
                name="trustType"
                control={control}
                rules={setValidForm("trustType")}
                render={({ field, fieldState }) => (
                  <Dropdown
                    filter
                    appendTo={"self"}
                    optionLabel="subitemName"
                    optionValue="subitemNo"
                    value={field.value}
                    defaultValue={""}
                    placeholder={t("plz_chs")}
                    name="trustType"
                    control={control}
                    options={trustTypeList}
                    className={`w-full ` + classNames({ "p-invalid": fieldState.error })}
                    disabled={disabledCondition}
                    onChange={(event) => {
                      field.onChange(event.value);
                    }}
                  />
                )}
              />
            </div>
            {/* 燃料熱值/碳含量來源 */}
            <div className="col-12 lg:col-4">
              <label htmlFor="fuelSource" className="form-label">
                {t("fuelSource")}
              </label>
              <FormInput
                disabled={disabledCondition}
                withLabel={false}
                register={register}
                errors={errors}
                id="fuelSource"
                type="text"
                enterClick={inputCheckKeyDown}
              />
            </div>
            {/* 燃料熱值/碳含量資料來源 */}
            <div className="col-12 lg:col-4">
              <label htmlFor="fuelDataSource" className="form-label">
                {t("fuelDataSource")}
              </label>
              <FormInput
                disabled={disabledCondition}
                withLabel={false}
                register={register}
                errors={errors}
                id="fuelDataSource"
                type="text"
                enterClick={inputCheckKeyDown}
              />
            </div>
            {/* 今年度有無使用該項設備 */}
            <div className="col-12 lg:col-4">
              <Controller
                name="isUseDevice"
                control={control}
                render={({ field, fieldState }) => (
                  <div className="flex align-items-center gap-2 h-full">
                    <Checkbox
                      disabled={disabledCondition}
                      inputId={field.name}
                      checked={field.value === "Y"}
                      inputRef={field.ref}
                      className={classNames({ "p-invalid": fieldState.error })}
                      onChange={(e) => {
                        field.onChange(e.checked ? "Y" : "N");
                      }}
                    />
                    <label htmlFor="isUseDevice">{t("isUseDevice")}</label>
                  </div>
                )}
              />
            </div>
            {/* 備註 */}
            <div className="col-12">
              <FormTextarea
                register={register}
                errors={errors}
                id="emissionDataRemark"
                name="emissionDataRemark"
                type="text"
                labelText={t("remark")}
                areaClass="w-full"
                disabled={disabledCondition}
                placeholder={parseFloat(watch("dataRatio")) < 100 ? t("pleaseStateReasonInRemark") : ""}
                rules={{
                  ...setValidForm("emissionDataRemark"),
                  validate: (value) => {
                    const ratio = parseFloat(watch("dataRatio"));
                    if (!isNaN(ratio) && ratio < 100) {
                      if (!value || value.trim() === "") {
                        return t("validation.required");
                      }
                    }
                    return true;
                  },
                }}
              />
            </div>
            {/* 新增 v0.75 活動數據之不確定性 06/13 */}
            <div className="col-12">
              <label className="form-label font-bold">{t("uncertainty_activityData")}</label>
            </div>
            <div className="col-12 grid">
              <div className="col-12 lg:col-6 grid">
                <div className="col-12 lg:col-6 ">
                  <label htmlFor="dataSaver" className="form-label ml-3">
                    {t("ciLower")}
                  </label>
                  <div className="flex gap-2 align-items-center flex-wrap lg:flex-nowrap">
                    <label className="form-label font-bold">-</label>
                    <FormNumberInput
                      wrapperStyle={{ width: "calc(100% - 40px)" }}
                      disabled={disabledCondition}
                      withLabel={false}
                      register={register}
                      errors={errors}
                      id="ciLower"
                      name="ciLower"
                      step="any"
                      enterClick={inputCheckKeyDown}
                      value={watch("ciLower")}
                      onChange={(newValue) => setValue("ciLower", newValue)}
                    />
                    <label className="form-label font-bold">%</label>
                  </div>
                </div>

                <div className="col-12 lg:col-6">
                  <label htmlFor="dataSaver" className="form-label ml-3">
                    {t("ciUpper")}
                  </label>
                  <div className="flex gap-2 align-items-center flex-wrap lg:flex-nowrap">
                    <label className="form-label font-bold">+</label>
                    <FormNumberInput
                      wrapperStyle={{ width: "calc(100% - 40px)" }}
                      disabled={disabledCondition}
                      withLabel={false}
                      register={register}
                      errors={errors}
                      id="ciUpper"
                      name="ciUpper"
                      step="any"
                      enterClick={inputCheckKeyDown}
                      value={watch("ciUpper")}
                      onChange={(newValue) => setValue("ciUpper", newValue)}
                    />

                    <label className="form-label font-bold">%</label>
                  </div>
                </div>

                <div className="col-12 lg:col-6">
                  <label htmlFor="dataSaver" className="form-label">
                    {t("uncertaintySource")}
                  </label>
                  <FormInput
                    disabled={disabledCondition}
                    withLabel={false}
                    register={register}
                    errors={errors}
                    id="uncertaintySource"
                    type="text"
                    enterClick={inputCheckKeyDown}
                  />
                </div>

                <div className="col-12 lg:col-6">
                  <label htmlFor="dataSaver" className="form-label">
                    {t("uncertaintySaver")}
                  </label>
                  <FormInput
                    disabled={disabledCondition}
                    withLabel={false}
                    register={register}
                    errors={errors}
                    id="uncertaintySaver"
                    type="text"
                    enterClick={inputCheckKeyDown}
                  />
                </div>
              </div>

              <div className="col-12 lg:col-6">
                <FileAttachmentHandler
                  id="uncertaintyFile"
                  label={`${t("attached_picture")}/${t("attachments")}`}
                  data={uncertaintyFile}
                  isEditable={disabledCondition}
                  fileInputRef={uncertaintyFileRef}
                  register={register}
                  errors={errors}
                  wrapperStyle={{ height: "8.5rem" }}
                  onFileChange={(e) => handleFileChange(e, uncertaintyFileRef, "uncertainty")}
                  onFileDelete={(e) => handleDeleteImg(e, uncertaintyFileRef, "uncertainty", uncertaintyFile?.fileId)}
                />
              </div>
            </div>

            <CycleDataTable
              identifySeq={itemObj?.data?.identifySeq}
              companySeq={itemObj?.companySeq}
              handleDeleteImg={handleDeleteImg}
              handleFileChange={handleFileChange}
              refs={refs}
              periodList={periodList}
              unit={unitWord}
              cycle={cycle}
              emissionUnitList={emissionUnitList}
              setRows={setRows}
              rows={rows}
              control={control}
              register={register}
              errors={errors}
              setValue={setValue}
              isEditable={disabledCondition}
            />

            {isApprove && (
              <div className="col-12 p-card bg-gray mt-4">
                <div className="p-card-body grid">
                  <div className="col-6">
                    <label className="form-label">&nbsp;</label>
                    <Controller
                      name="action"
                      control={control}
                      render={({ field, fieldState }) => (
                        <SelectButton
                          value={field.value}
                          onChange={(e) => field.onChange(e.value)}
                          options={approveOptions}
                          disabled={!isEditable}
                        />
                      )}
                    />
                  </div>
                  <div className="col-6">
                    <FormTextarea
                      register={register}
                      errors={errors}
                      id="memo"
                      name="memo"
                      type="text"
                      labelText={t("approveMemo")}
                      areaClass="w-full"
                      disabled={!isEditable}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="col-12 gap-2 flex-all-center">
              {!isApprove && (
                <Button
                  loading={confirmLoad}
                  disabled={action === "create" ? !(isValid && isDirty) : !isValid || disabledCondition}
                  type="submit"
                  label={itemObj?.data?.dataList?.length > 0 ? t("button.update") : t("button.create")}
                  size="small"
                />
              )}

              {isApprove && (
                <Button
                  severity="danger"
                  type="button"
                  disabled={!isNotBlank(watch("action"))}
                  label={t("button.approve")}
                  size="small"
                  onClick={axios_approveWork}
                />
              )}
            </div>
          </form>
        </div>
      </div>

      {/* 刪除 */}
      <PrimeConfirmDialog
        visible={delDialog}
        onClose={() => {
          refuseDelete();
        }}
        onConfirm={() => {
          confirmDel();
        }}
        title={t("button.delete")}
        bodyTemplate={t("activeData_del_content")}
      />

      <PrimeDialog
        isShowConfBtn={false}
        scrollByBody={true}
        visible={detailDialog}
        onClose={() => {
          setDetailDialog(false);
        }}
        onConfirm={() => {
          setDetailDialog(false);
        }}
        title={itemObj?.data?.sourceName}
        size="lg"
        bodyTemplate={
          <>
            <CoefficientInfo coefficientObj={coefficientObj} />
          </>
        }
      />
    </>
  );
};

export default ActivityData_Detail;
